import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import api, { saveSession } from "@/lib/api";
import { Briefcase, Calculator } from "lucide-react";

export default function PortalSelect() {
  const nav = useNavigate();
  const firm_token = localStorage.getItem("firm_token");
  const [role, setRole] = useState(null); // owner / accountant
  const [creds, setCreds] = useState({ username: "", password: "" });
  const [busy, setBusy] = useState(false);
  if (!firm_token) return <Navigate to="/firm/login" replace />;

  const login = async () => {
    if (!creds.username || !creds.password) { toast.error("Enter both fields"); return; }
    setBusy(true);
    try {
      const company_id = localStorage.getItem("company_id") || null;
      const { data } = await api.post("/firms/portal-login", {
        firm_token, role, username: creds.username, password: creds.password,
        company_id,
      });
      saveSession({ token: data.token, role: data.role, firm_name: data.firm_name });
      if (data.company_id) {
        localStorage.setItem("company_id", data.company_id);
      }
      toast.success(`Welcome, ${creds.username}`);
      nav("/app/dashboard");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal login failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-white border border-border rounded-lg p-8">
        <div className="overline mb-2">Select Portal</div>
        <h1 className="text-3xl font-bold tracking-tight">
          {localStorage.getItem("firm_token_name")}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Choose Owner or Accountant portal and enter your credentials.</p>

        {!role && (
          <div className="grid sm:grid-cols-2 gap-4 mt-8">
            <button
              onClick={() => setRole("owner")}
              data-testid="portal-owner-btn"
              className="border border-border rounded-lg p-6 text-left hover:border-[#064E3B] hover:bg-slate-50 transition-colors">
              <Briefcase className="text-[#064E3B] mb-2" />
              <div className="text-lg font-bold">Owner / CA Portal</div>
              <div className="text-xs text-muted-foreground mt-1">Read-only overview of reports, add accountants, banks, loans.</div>
            </button>
            <button
              onClick={() => setRole("accountant")}
              data-testid="portal-accountant-btn"
              className="border border-border rounded-lg p-6 text-left hover:border-[#064E3B] hover:bg-slate-50 transition-colors">
              <Calculator className="text-[#064E3B] mb-2" />
              <div className="text-lg font-bold">Accountant Portal</div>
              <div className="text-xs text-muted-foreground mt-1">Day-to-day operations: sales, purchases, POS, challan, reports.</div>
            </button>
          </div>
        )}

        {role && (
          <div className="mt-6 space-y-4">
            <div className="chip chip-slate uppercase text-[10px]">{role} portal</div>
            <div>
              <Label>Username</Label>
              <Input value={creds.username} onChange={(e) => setCreds({ ...creds, username: e.target.value })} data-testid="portal-username" />
            </div>
            <div>
              <Label>Password</Label>
              <Input type="password" value={creds.password} onChange={(e) => setCreds({ ...creds, password: e.target.value })} data-testid="portal-password" />
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setRole(null)}>Back</Button>
              <Button className="btn-primary" disabled={busy} onClick={login} data-testid="portal-login-submit">
                {busy ? "..." : `Enter ${role} portal`}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
